import { MijnTeamsChips } from "./MijnTeams";
import { TeamBerichtenPanel } from "./TeamBerichten";
import { DashboardAanpassen, DashboardTegel, useDashboardIndeling } from "./DashboardTegels";
import { AiTegelInhoud, useAiTegels } from "./schermbouwer/AiTegels";
import { useCustom, useGetIdentity, useGetLocale, useTranslate } from "@refinedev/core";
import { useState } from "react";
import type { ReactNode } from "react";

import { DashIcon } from "./dashboardIcons";
import { lineChart, gauge, donut } from "./dashboardSvg";
import "./Dashboard.css";

// Fawkes Command Center — the /home landing dashboard, migrated from the Jmix `FawkesHomeView`. A
// faithful port: hero (greeting + quick actions), a KPI row, a content grid (live operations with the
// intake/outflow line chart + capacity gauge, occupancy donut, expired-vaccination panel, actions &
// alerts, today, SLA) and a six-tile workspaces grid. All numbers come from `GET /api/dashboard`; the
// SPA renders dumbly (greeting time-of-day, euro formatting, delta arrows and the workspaces grid are
// derived here). Each section degrades to an honest empty-state, exactly as the Jmix view did.

const CHART_DAYS = 7;
const HIGH_OCCUPANCY_PCT = 90;
const SHORT_MONTHS_NL = [
  "jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec",
];

type Slice = { label: string; value: number };
type DashboardData = {
  vestigingSelected: boolean;
  vestigingNaam: string | null;
  modules: { asiel: boolean; kennelplanning: boolean; shelterExchanges: boolean };
  kpis: {
    animalsPresent: number | null;
    intakesToday: number | null;
    releasesToday: number | null;
    openInvoices: number | null;
  };
  trend: { days: string[]; intake: number[]; outflow: number[] } | null;
  capacity: { present: number; capacity: number; species: Slice[] } | null;
  occupancy: Slice[];
  vaccinations: {
    verlopen: number;
    vandaag: number;
    binnen7: number;
    perDiersoort: { soort: string; aantal: number }[];
    hoogstePrioriteit: string | null;
  };
  longStay: number;
  invoices: { openTotal: number; overdueCount: number; overdueAmount: number };
};

export interface DashboardProps {
  // Navigate to a Jmix route name (the SPA maps migrated targets to in-app pages, the rest hand off).
  onNavigate: (route: string) => void;
  onNewAnimal: () => void;
  onInboeken: () => void;
  onUitboeken: () => void;
  /** Een dier openen, vanuit een taak in Actions & alerts. */
  onOpenAnimal?: (id: string) => void;
}

/** De tabs van Actions & alerts. */
type AlertTab = "all" | "tasks" | "financial" | "alerts";

export function Dashboard({ onNavigate: navigeer, onNewAnimal, onInboeken, onUitboeken, onOpenAnimal }: DashboardProps) {
  const t = useTranslate();
  // Het dashboard telt alle aanwezige dieren, ook die in een pleeggezin. Elke link naar "Dier aanwezig" opent
  // dat overzicht daarom met de pleeggezinnen erbij: het getal hier en de lijst daar horen overeen te komen
  // (Maarten, 2026-10-11: "als er 1 staat wil ik er ook 1 zien").
  const onNavigate: DashboardProps["onNavigate"] = (route) => {
    if (route === "dier-attendances") zetStart("dierAanwezig.pleeg", "beide");
    navigeer(route);
  };
  // De tabs boven Actions & alerts waren tekst zonder klik (Alma, 2026-10-09: "ik kan nergens op klikken").
  const [alertTab, setAlertTab] = useState<AlertTab>("all");
  // Welke tegels deze gebruiker aan heeft staan en in welke volgorde.
  const indeling = useDashboardIndeling();
  const aiTegels = useAiTegels();
  const [aanpassen, setAanpassen] = useState(false);
  const { data: identity } = useGetIdentity<{ id: string; name?: string; email?: string }>();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading } = useCustom({ url: "dashboard", method: "get" });
  const d = data?.data as DashboardData | undefined;

  if (isLoading || !d) {
    return <div className="fcc fcc--loading" aria-busy="true" />;
  }

  const asiel = d.modules.asiel;
  const cap = d.capacity;
  const capPercent = cap && cap.capacity > 0 ? Math.round((cap.present * 100) / cap.capacity) : 0;
  const hasCapacity = !!cap && cap.capacity > 0;

  // ---- derived numbers (from the 30-day trend) -------------------------------
  const intakeSeries = d.trend?.intake ?? [];
  const outflowSeries = d.trend?.outflow ?? [];
  const netToday =
    intakeSeries.length > 0
      ? intakeSeries[intakeSeries.length - 1] - outflowSeries[outflowSeries.length - 1]
      : null;
  const intakeDelta = lastVsPrev(intakeSeries);
  const releaseDelta = lastVsPrev(outflowSeries);

  const greetingName = (identity?.name ?? "").trim();
  const greeting = greetingText(t, greetingName);
  const subtitle = buildSubtitle(t, d.vestigingNaam, locale);

  const overdueCount = d.invoices.overdueCount;

  // SLA rows (computed first so the badge can report the count).
  const slaRows: { icon: string; title: string; sub: string; route: string }[] = [];
  if (overdueCount > 0) {
    slaRows.push({
      icon: "file-text",
      title: `${overdueCount} ${t("dashboard.sla.overdueSuffix")}`,
      sub: `${t("dashboard.sla.overdueSub")} ${formatEuro(d.invoices.openTotal)}`,
      route: "facturen",
    });
  }
  if (d.longStay > 0) {
    slaRows.push({
      icon: "clock",
      title: `${d.longStay} ${t("dashboard.sla.longStaySuffix")}`,
      sub: t("dashboard.sla.longStaySub"),
      route: "dier-attendances",
    });
  }
  if (hasCapacity && capPercent >= HIGH_OCCUPANCY_PCT) {
    slaRows.push({
      icon: "chart",
      title: `${t("dashboard.capacity.occupancyLabel")} ${capPercent}%`,
      sub: t("dashboard.alert.highOccSub"),
      route: "dier-attendances",
    });
  }

  const planningRoute = d.modules.kennelplanning ? "kennelplanning" : "dier-attendances";

  return (
    <div className="fcc">
      {/* ---- Hero ---- */}
      <div className="fcc-hero">
        <div className="fcc-hero-row">
          <div className="fcc-hero-copy">
            <h1 className="fcc-hero-title">{greeting} 👋</h1>
            <p className="fcc-hero-sub">{subtitle}</p>
            <MijnTeamsChips />
          </div>
          <div className="fcc-hero-actions">
            <HeroButton variant="primary" icon="plus" label={t("dashboard.action.newAnimal")} onClick={onNewAnimal} />
            {asiel && (
              <>
                <HeroButton variant="soft" icon="sign-in" label={t("dashboard.action.inboeken")} onClick={onInboeken} />
                <HeroButton variant="soft" icon="sign-out" label={t("dashboard.action.uitboeken")} onClick={onUitboeken} />
              </>
            )}
          </div>
        </div>
        {!d.vestigingSelected && (
          <div className="fcc-banner" role="status">
            {t("dashboard.banner.noVestiging")}
          </div>
        )}
      </div>

      {/* ---- KPI cards ---- */}
      <div className="fcc-kpis" role="list">
        {asiel && (
          <>
            <KpiCard
              tone="green"
              icon="clipboard-check"
              value={kpiValue(d.kpis.animalsPresent)}
              label={t("dashboard.kpi.ANIMALS_PRESENT")}
              sub={deltaText(t, netToday)}
              onClick={() => onNavigate("dier-attendances")}
            />
            <KpiCard
              tone="blue"
              icon="sign-in"
              value={kpiValue(d.kpis.intakesToday)}
              label={t("dashboard.kpi.INTAKES_TODAY")}
              sub={deltaText(t, intakeDelta)}
              onClick={() => onNavigate("dier-attendances")}
            />
            <KpiCard
              tone="purple"
              icon="heart"
              value={kpiValue(d.kpis.releasesToday)}
              label={t("dashboard.kpi.RELEASES_TODAY")}
              sub={deltaText(t, releaseDelta)}
              onClick={() => onNavigate("dier-placements")}
            />
          </>
        )}
        <KpiCard
          tone="orange"
          icon="invoice"
          value={kpiValue(d.kpis.openInvoices)}
          label={t("dashboard.kpi.OPEN_INVOICES")}
          sub={formatEuro(d.invoices.openTotal)}
          onClick={() => onNavigate("facturen")}
        />
        <div className="fcc-kpi fcc-kpi--gray">
          <div className="fcc-kpi-icon">
            <DashIcon name="shield" />
          </div>
          <div className="fcc-kpi-copy">
            <span className="fcc-kpi-label">{t("dashboard.metric.systemStatus")}</span>
            <span className="fcc-kpi-sub">{t("dashboard.metric.operational")}</span>
          </div>
        </div>
      </div>

      {/* ---- Content grid ---- */}
      <div className="fcc-grid-kop">
        <button type="button" className="fcc-aanpassen" onClick={() => setAanpassen(true)}>
          <span aria-hidden="true">⚙</span> {t("dashboard.aanpassen.knop")}
        </button>
      </div>
      {aanpassen && <DashboardAanpassen indeling={indeling} asiel={asiel} onSluit={() => setAanpassen(false)} />}
      <div className="fcc-grid">
        {asiel && (
          <>
            {/* Operation panel: chart + capacity gauge */}
            <DashboardTegel code="operatie" breed indeling={indeling}>
            <div className="fcc-panel fcc-panel--operation">
              <div className="fcc-panel-head">
                <h2 className="fcc-panel-title">{t("dashboard.section.operation")}</h2>
                <div className="fcc-live">
                  <span />
                  <span>{t("dashboard.live")}</span>
                </div>
              </div>
              <div className="fcc-op-layout">
                <ChartBlock t={t} trend={d.trend} />
                <CapacityBlock t={t} capacity={cap} percent={capPercent} hasCapacity={hasCapacity} delta={deltaText(t, netToday)} onNavigate={onNavigate} />
              </div>
            </div>
            </DashboardTegel>

            {/* Locations panel: occupancy donut */}
            <DashboardTegel code="locaties" indeling={indeling}>
              <LocationsPanel t={t} occupancy={d.occupancy} onNavigate={onNavigate} />
            </DashboardTegel>

            {/* Entingen panel: expired vaccinations */}
            <DashboardTegel code="entingen" indeling={indeling}>
              <EntingenPanel t={t} v={d.vaccinations} onNavigate={onNavigate} />
            </DashboardTegel>

            {/* Gewichtssignalen: dieren onder of boven hun streefgewicht, of met een verlopen weging */}
            <DashboardTegel code="gewicht" indeling={indeling}>
              <GewichtPanel t={t} onNavigate={onNavigate} />
            </DashboardTegel>
          </>
        )}

        {/* Actions & alerts */}
        <DashboardTegel code="acties" indeling={indeling}>
        <div className="fcc-panel fcc-panel--alerts">
          <div className="fcc-panel-head">
            <h2 className="fcc-panel-title">{t("dashboard.section.actions")}</h2>
          </div>
          <div className="fcc-tabs" role="tablist">
            {(["all", "tasks", "financial", "alerts"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={alertTab === tab}
                className={`fcc-tab${alertTab === tab ? " is-active" : ""}`}
                onClick={() => setAlertTab(tab)}
              >
                {t(`dashboard.tab.${tab}`)}
              </button>
            ))}
          </div>
          {(alertTab === "all" || alertTab === "tasks") && <OpenTaken t={t} onOpenAnimal={onOpenAnimal} />}
          {(alertTab === "all" || alertTab === "alerts") && (<>
          <span className="fcc-subhead">{t("dashboard.actions.priorityTasks")}</span>
          {(() => {
            const tasks: ReactNode[] = [];
            if (hasCapacity && capPercent >= HIGH_OCCUPANCY_PCT) {
              tasks.push(
                <Alert key="occ" tone="amber" icon="warning" title={`${t("dashboard.capacity.occupancyLabel")} ${capPercent}%`} sub={t("dashboard.alert.highOccSub")} onClick={() => onNavigate("dier-attendances")} />,
              );
            }
            if (d.longStay > 0) {
              tasks.push(
                <Alert key="long" tone="amber" icon="clock" title={`${d.longStay} ${t("dashboard.sla.longStaySuffix")}`} sub={t("dashboard.sla.longStaySub")}
                  // De lijst erachter toont precies wat hier geteld is: langer dan 60 dagen = minstens 61, pleeggezinnen erbij.
                  onClick={() => { zetStart("dierAanwezig.minDagen", "61"); onNavigate("dier-attendances"); }} />,
              );
            }
            return tasks.length > 0 ? tasks : <div className="fcc-note">{t("dashboard.actions.noTasks")}</div>;
          })()}
          </>)}
          {(alertTab === "all" || alertTab === "financial") && (<>
          <span className="fcc-subhead">{t("dashboard.actions.financialHeading")}</span>
          {overdueCount > 0 ? (
            <Alert
              tone="red"
              icon="file-text"
              title={`${overdueCount} ${t("dashboard.sla.overdueSuffix")}`}
              sub={t("dashboard.actions.overdueSub")}
              value={formatEuro(d.invoices.overdueAmount)}
              // Financieel › Overzichten kan op "vervallen" filteren en telt dan hetzelfde aantal en bedrag.
              onClick={() => {
                zetStart("factuurOverzicht.start", JSON.stringify({ status: "VERVALLEN", periode: "alles", vestiging: d.vestigingNaam }));
                onNavigate("financieel-overzichten");
              }}
            />
          ) : (
            <div className="fcc-note">{t("dashboard.actions.noFinancial")}</div>
          )}
          <LinkRow label={t("dashboard.actions.link")} onClick={() => onNavigate("facturen")} />
          </>)}
        </div>
        </DashboardTegel>

        {/* Berichten aan teams: sturen, lezen en reageren */}
        <DashboardTegel code="berichten" indeling={indeling}>
          <TeamBerichtenPanel />
        </DashboardTegel>

        {/* Today (honest empty-state) */}
        <DashboardTegel code="vandaag" indeling={indeling}>
        <div className="fcc-panel fcc-panel--today">
          <div className="fcc-panel-head">
            <h2 className="fcc-panel-title">{t("dashboard.section.today")}</h2>
          </div>
          <div className="fcc-empty-rich">
            <div className="fcc-empty-ic">
              <DashIcon name="calendar" />
            </div>
            <span className="fcc-empty-title">{t("dashboard.today.emptyTitle")}</span>
            <span className="fcc-empty-sub">{t("dashboard.today.emptySub")}</span>
          </div>
          <LinkRow label={t("dashboard.today.link")} onClick={() => onNavigate(planningRoute)} />
        </div>
        </DashboardTegel>

        {/* SLA panel */}
        <DashboardTegel code="sla" indeling={indeling}>
        <div className="fcc-panel fcc-panel--sla">
          <div className="fcc-panel-head">
            <h2 className="fcc-panel-title">{t("dashboard.section.sla")}</h2>
            <div className="fcc-badge">
              {slaRows.length} {t("dashboard.sla.attentionSuffix")}
            </div>
          </div>
          {slaRows.length === 0 ? (
            <div className="fcc-note">{t("dashboard.sla.allClear")}</div>
          ) : (
            <>
              <div className="fcc-sla">
                {slaRows.map((r, i) => (
                  <SlaRow key={i} t={t} icon={r.icon} title={r.title} sub={r.sub} onClick={() => onNavigate(r.route)} />
                ))}
              </div>
              <LinkRow label={t("dashboard.sla.link")} onClick={() => onNavigate("facturen")} />
            </>
          )}
        </div>
        </DashboardTegel>

        {/* Tegels die hier niet in code staan maar door een collega zijn ontworpen en gepubliceerd.
            Ze staan in hetzelfde raster en doen mee met dezelfde indeling: wie ze niet wil, zet ze
            uit zoals elke andere tegel. */}
        {aiTegels.map((tegel) => (
          <DashboardTegel key={tegel.tegelCode} code={tegel.tegelCode} indeling={indeling}>
            <div className="fcc-panel">
              <div className="fcc-panel-head">
                <h2 className="fcc-panel-title">{tegel.naam}</h2>
              </div>
              <AiTegelInhoud tegel={tegel} />
            </div>
          </DashboardTegel>
        ))}
      </div>

      {/* ---- Workspaces ---- */}
      <div className="fcc-ws">
        <h2 className="fcc-ws-title">{t("dashboard.workspaces")}</h2>
        <div className="fcc-ws-grid" role="list">
          <WsCard t={t} tone="green" icon="heart" titleKey="dashboard.ws.animals" descKey="dashboard.ws.animals.desc" onClick={() => onNavigate("animal")} />
          <WsCard t={t} tone="blue" icon="users" titleKey="dashboard.ws.relations" descKey="dashboard.ws.relations.desc" onClick={() => onNavigate("relaties")} />
          {d.modules.kennelplanning && (
            <WsCard t={t} tone="sand" icon="calendar" titleKey="dashboard.ws.planning" descKey="dashboard.ws.planning.desc" onClick={() => onNavigate("kennelplanning")} />
          )}
          {d.modules.shelterExchanges && (
            <WsCard t={t} tone="mint" icon="exchange" titleKey="dashboard.ws.exchanges" descKey="dashboard.ws.exchanges.desc" onClick={() => onNavigate("uitwisselingen")} />
          )}
          <WsCard t={t} tone="cream" icon="invoice" titleKey="dashboard.ws.invoices" descKey="dashboard.ws.invoices.desc" onClick={() => onNavigate("facturen")} />
          <WsCard t={t} tone="gray" icon="cog" titleKey="dashboard.ws.settings" descKey="dashboard.ws.settings.desc" onClick={() => onNavigate("instellingen")} />
        </div>
      </div>
    </div>
  );
}

// ---- sub-components --------------------------------------------------------

type T = (key: string) => string;

function HeroButton({ variant, icon, label, onClick }: { variant: "primary" | "soft"; icon: string; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`fcc-btn fcc-btn--${variant}`} onClick={onClick}>
      <DashIcon name={icon} />
      <span>{label}</span>
    </button>
  );
}

function KpiCard({ tone, icon, value, label, sub, onClick }: { tone: string; icon: string; value: string; label: string; sub?: string; onClick?: () => void }) {
  return (
    <Activatable className={`fcc-kpi fcc-kpi--${tone}${onClick ? " fcc-clickable" : ""}`} ariaLabel={label} onClick={onClick} role="listitem">
      <div className="fcc-kpi-icon">
        <DashIcon name={icon} />
      </div>
      <div className="fcc-kpi-copy">
        <span className="fcc-kpi-value">{value}</span>
        <span className="fcc-kpi-label">{label}</span>
        {sub && <span className="fcc-kpi-sub">{sub}</span>}
      </div>
    </Activatable>
  );
}

function ChartBlock({ t, trend }: { t: T; trend: DashboardData["trend"] }) {
  const empty = !trend || trend.days.length === 0;
  let chartHtml = "";
  if (!empty) {
    const intake = lastN(trend!.intake, CHART_DAYS);
    const outflow = lastN(trend!.outflow, CHART_DAYS);
    const labels = lastN(trend!.days, CHART_DAYS).map(formatShortDay);
    const max = niceCeil(Math.max(maxOf(intake), maxOf(outflow)));
    chartHtml = lineChart(labels, intake, outflow, max);
  }
  return (
    <div className="fcc-chart-wrap">
      <div className="fcc-chart-head">
        <span className="fcc-chart-title">{t("dashboard.chart.title")}</span>
        <span className="fcc-chart-pill">{t("dashboard.chart.period7")}</span>
      </div>
      {empty ? (
        <div className="fcc-note">{t("dashboard.chart.empty")}</div>
      ) : (
        <>
          <div className="fcc-chart" dangerouslySetInnerHTML={{ __html: chartHtml }} />
          <div className="fcc-legend">
            <div className="fcc-legend-item">
              <span className="fcc-legend-dot fcc-legend-dot--in" />
              <span>{t("dashboard.chart.trend.intake")}</span>
            </div>
            <div className="fcc-legend-item">
              <span className="fcc-legend-dot fcc-legend-dot--out" />
              <span>{t("dashboard.chart.trend.outflow")}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CapacityBlock({ t, capacity, percent, hasCapacity, delta, onNavigate }: { t: T; capacity: DashboardData["capacity"]; percent: number; hasCapacity: boolean; delta: string; onNavigate: (r: string) => void }) {
  const empty = !capacity || (capacity.present === 0 && capacity.species.length === 0);
  return (
    <div className="fcc-capacity">
      <h3 className="fcc-capacity-title">{t("dashboard.capacity.title")}</h3>
      {empty ? (
        <div className="fcc-note">{t("dashboard.chart.empty")}</div>
      ) : (
        <>
          <div className="fcc-gauge-wrap">
            <div dangerouslySetInnerHTML={{ __html: gauge(capacity!.present, capacity!.capacity) }} />
            <div className="fcc-gauge-content">
              {hasCapacity ? (
                <>
                  <span className="fcc-gauge-value">{capacity!.present} / {capacity!.capacity}</span>
                  <span className={`fcc-gauge-label${percent > 100 ? " fcc-gauge-label--over" : ""}`}>
                    {t("dashboard.capacity.occupancyLabel")} {percent}%
                  </span>
                </>
              ) : (
                <>
                  <span className="fcc-gauge-value">{capacity!.present}</span>
                  <span className="fcc-gauge-label">{t("dashboard.capacity.present")}</span>
                </>
              )}
              {delta && <span className="fcc-gauge-delta">{delta}</span>}
            </div>
          </div>
          <div className="fcc-mini">
            {capacity!.species.map((s, i) => (
              <StatRow key={i} idx={i} label={s.label} value={s.value} pct={Math.round((s.value * 100) / Math.max(1, capacity!.present))} />
            ))}
          </div>
          <LinkRow label={t("dashboard.capacity.link")} onClick={() => onNavigate("dier-attendances")} />
        </>
      )}
    </div>
  );
}

function LocationsPanel({ t, occupancy, onNavigate }: { t: T; occupancy: Slice[]; onNavigate: (r: string) => void }) {
  const total = occupancy.reduce((a, s) => a + s.value, 0);
  return (
    <div className="fcc-panel fcc-panel--locations">
      <div className="fcc-panel-head">
        <h2 className="fcc-panel-title">{t("dashboard.chart.occupancy")}</h2>
      </div>
      {occupancy.length === 0 ? (
        <div className="fcc-note">{t("dashboard.chart.empty")}</div>
      ) : (
        <>
          <div className="fcc-loc-body">
            <div className="fcc-donut-wrap">
              <div dangerouslySetInnerHTML={{ __html: donut(occupancy, total) }} />
              <div className="fcc-donut-center">
                <span className="fcc-donut-cap">{t("dashboard.occupancy.totalLabel")}</span>
                <span className="fcc-donut-total">{total}</span>
                <span className="fcc-donut-cap">{t("dashboard.occupancy.present")}</span>
              </div>
            </div>
            <div className="fcc-loc-list">
              {occupancy.map((s, i) => (
                <StatRow key={i} idx={i} label={s.label} value={s.value} pct={total > 0 ? Math.round((s.value * 100) / total) : 0} />
              ))}
            </div>
          </div>
          <LinkRow label={t("dashboard.locations.link")} onClick={() => onNavigate("dier-attendances")} />
        </>
      )}
    </div>
  );
}

function EntingenPanel({ t, v, onNavigate }: { t: T; v: DashboardData["vaccinations"]; onNavigate: (r: string) => void }) {
  const allClear = v.verlopen === 0 && v.vandaag === 0 && v.binnen7 === 0;
  return (
    <div className="fcc-panel fcc-panel--vacc">
      <div className="fcc-panel-head">
        <h2 className="fcc-panel-title">{t("dashboard.vacc.title")}</h2>
        {v.verlopen > 0 && <div className="fcc-badge">{t("dashboard.vacc.attention")}</div>}
      </div>
      {allClear ? (
        <div className="fcc-note">{t("dashboard.vacc.allClear")}</div>
      ) : (
        <div className="fcc-vacc-body">
          <div className="fcc-vacc-main">
            <div className="fcc-vacc-ic">
              <DashIcon name="drop" />
            </div>
            <div className="fcc-vacc-figures">
              <span className="fcc-vacc-value">{v.verlopen}</span>
              <div className="fcc-vacc-subs">
                {v.vandaag > 0 && <span className="fcc-vacc-today">{v.vandaag} {t("dashboard.vacc.today")}</span>}
                {v.binnen7 > 0 && <span className="fcc-vacc-sub">{v.binnen7} {t("dashboard.vacc.within7")}</span>}
              </div>
            </div>
          </div>
          <div className="fcc-vacc-break">
            {v.perDiersoort.map((st, i) => (
              <div className="fcc-vacc-row" key={i}>
                <span className="fcc-vacc-rname">{st.soort}</span>
                <span className="fcc-vacc-rval">{st.aantal}</span>
              </div>
            ))}
            {v.hoogstePrioriteit && (
              <div className="fcc-vacc-prio">
                <span>{t("dashboard.vacc.priority")} </span>
                <span className="fcc-vacc-prio-name">{v.hoogstePrioriteit}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <LinkRow label={t("dashboard.vacc.link")} onClick={() => onNavigate("entingen-overzicht")} />
    </div>
  );
}

/**
 * Gewichtssignalen (Alma, 2026-10-09: "hoe zie ik dat dit dier onder gewicht is, moet ik naar elk dier toe?"): de
 * aanwezige dieren onder of boven hun streefgewicht, en bij hoeveel de weging verlopen is. De link opent Dieren
 * aanwezig met het filter "buiten streefgewicht" al gezet.
 */
function GewichtPanel({ t, onNavigate }: { t: T; onNavigate: (r: string) => void }) {
  const { data } = useCustom<{ buiten: number; onder: number; boven: number; verlopen: number; aanwezig: number }>({
    url: "animals/present/gewichtssignalen", method: "get",
  });
  const g = data?.data;
  const naarOverzicht = (filter: string) => {
    try {
      sessionStorage.setItem("dierAanwezig.gewichtFilter", filter);
    } catch {
      /* zonder sessionStorage opent het overzicht gewoon zonder filter */
    }
    onNavigate("dier-attendances");
  };
  return (
    <div className="fcc-panel fcc-panel--vacc fcc-panel--gewicht">
      <div className="fcc-panel-head">
        <h2 className="fcc-panel-title">{t("dashboard.gewicht.title")}</h2>
        {g && g.buiten > 0 && <div className="fcc-badge">{t("dashboard.vacc.attention")}</div>}
      </div>
      {!g ? (
        <div className="fcc-note">…</div>
      ) : g.buiten === 0 && g.verlopen === 0 ? (
        <div className="fcc-note">{t("dashboard.gewicht.allClear")}</div>
      ) : (
        <div className="fcc-vacc-body">
          <button type="button" className="fcc-vacc-main fcc-gewicht-main" onClick={() => naarOverzicht("BUITEN")}>
            <div className="fcc-vacc-ic">
              <DashIcon name="warning" />
            </div>
            <div className="fcc-vacc-figures">
              <span className="fcc-vacc-value">{g.buiten}</span>
              <span className="fcc-vacc-sub">{t("dashboard.gewicht.buiten")}</span>
            </div>
          </button>
          <div className="fcc-vacc-break">
            <button type="button" className="fcc-vacc-row fcc-gewicht-row" onClick={() => naarOverzicht("ONDER")}>
              <span className="fcc-vacc-rname">↓ {t("dashboard.gewicht.onder")}</span>
              <span className="fcc-vacc-rval">{g.onder}</span>
            </button>
            <button type="button" className="fcc-vacc-row fcc-gewicht-row" onClick={() => naarOverzicht("BOVEN")}>
              <span className="fcc-vacc-rname">↑ {t("dashboard.gewicht.boven")}</span>
              <span className="fcc-vacc-rval">{g.boven}</span>
            </button>
            <button type="button" className="fcc-vacc-row fcc-gewicht-row" onClick={() => naarOverzicht("VERLOPEN")}>
              <span className="fcc-vacc-rname">◷ {t("dashboard.gewicht.verlopen")}</span>
              <span className="fcc-vacc-rval">{g.verlopen}</span>
            </button>
          </div>
        </div>
      )}
      <LinkRow label={t("dashboard.gewicht.link")} onClick={() => naarOverzicht("BUITEN")} />
    </div>
  );
}

/**
 * De open taken onder Actions & alerts: eerst die van je eigen team, anders alle. Een klik opent het dier, waar de
 * taak uitgevoerd wordt.
 */
function OpenTaken({ t, onOpenAnimal }: { t: T; onOpenAnimal?: (id: string) => void }) {
  const mijn = useCustom<{ taken: { id: string; dierId: number | null; dierNaam: string | null; title: string; overdue: boolean; dueToday: boolean; teamLabel: string | null }[]; mijnTeams?: string[] }>({
    url: "taken?status=OPEN&mijnTeams=true&size=5", method: "get",
  });
  const taken = Array.isArray(mijn.data?.data?.taken) ? mijn.data!.data.taken : [];
  const heeftTeam = (mijn.data?.data?.mijnTeams ?? []).length > 0;
  return (
    <>
      <span className="fcc-subhead">{t(heeftTeam ? "dashboard.taken.mijnTeam" : "dashboard.taken.open")}</span>
      {taken.length === 0 ? (
        <div className="fcc-note">{t("dashboard.taken.geen")}</div>
      ) : (
        taken.map((taak) => (
          <Alert
            key={taak.id}
            tone={taak.overdue ? "red" : "amber"}
            icon="clock"
            title={taak.title}
            sub={[taak.dierNaam, taak.teamLabel, taak.overdue ? t("dashboard.taken.achterstallig") : taak.dueToday ? t("dashboard.taken.vandaag") : null]
              .filter(Boolean).join(" · ")}
            onClick={taak.dierId != null && onOpenAnimal ? () => onOpenAnimal(String(taak.dierId)) : undefined}
          />
        ))
      )}
    </>
  );
}

function StatRow({ idx, label, value, pct }: { idx: number; label: string; value: number; pct: number }) {
  return (
    <div className="fcc-stat-row">
      <span className={`fcc-dot fcc-c${idx % 6}`} />
      <span className="fcc-stat-name">{label}</span>
      <span className="fcc-stat-val">{value}</span>
      <span className="fcc-stat-pct">{pct}%</span>
    </div>
  );
}

function Alert({ tone, icon, title, sub, value, onClick }: { tone: string; icon: string; title: string; sub?: string; value?: string; onClick?: () => void }) {
  return (
    <Activatable className={`fcc-alert fcc-alert--${tone}${onClick ? " fcc-clickable" : ""}`} ariaLabel={title} onClick={onClick}>
      <div className="fcc-alert-ic">
        <DashIcon name={icon} />
      </div>
      <div className="fcc-alert-body">
        <span className="fcc-alert-title">{title}</span>
        {sub && <span className="fcc-alert-sub">{sub}</span>}
      </div>
      {value && <span className="fcc-alert-value">{value}</span>}
    </Activatable>
  );
}

function SlaRow({ t, icon, title, sub, onClick }: { t: T; icon: string; title: string; sub: string; onClick: () => void }) {
  return (
    <Activatable className="fcc-sla-row fcc-clickable" ariaLabel={title} onClick={onClick}>
      <div className="fcc-sla-ic">
        <DashIcon name={icon} />
      </div>
      <div className="fcc-sla-body">
        <span className="fcc-sla-title">{title}</span>
        <span className="fcc-sla-sub">{sub}</span>
      </div>
      <span className="fcc-sla-action">{t("dashboard.sla.view")}</span>
    </Activatable>
  );
}

function WsCard({ t, tone, icon, titleKey, descKey, onClick }: { t: T; tone: string; icon: string; titleKey: string; descKey: string; onClick: () => void }) {
  return (
    <Activatable className={`fcc-ws-card fcc-ws-card--${tone}`} ariaLabel={t(titleKey)} onClick={onClick} role="listitem">
      <div className="fcc-ws-ic">
        <DashIcon name={icon} />
      </div>
      <div className="fcc-ws-body">
        <h3 className="fcc-ws-card-title">{t(titleKey)}</h3>
        <p className="fcc-ws-card-desc">{t(descKey)}</p>
      </div>
      <div className="fcc-ws-arrow">
        <DashIcon name="arrow-right" />
      </div>
    </Activatable>
  );
}

function LinkRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="fcc-link" onClick={onClick}>
      <span>{label}</span>
      <DashIcon name="arrow-right" />
    </button>
  );
}

// A clickable card/row rendered as a real <button> so keyboard activation comes for free; the UA button
// look is reset in CSS (.fcc-as-button). When onClick is absent it renders a plain div.
function Activatable({ className, ariaLabel, onClick, role, children }: { className: string; ariaLabel: string; onClick?: () => void; role?: string; children: ReactNode }) {
  if (!onClick) {
    return (
      <div className={className} role={role}>
        {children}
      </div>
    );
  }
  return (
    <button type="button" className={`${className} fcc-as-button`} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </button>
  );
}

// ---- pure helpers ----------------------------------------------------------

function kpiValue(v: number | null): string {
  return v == null ? "—" : String(v);
}

function lastVsPrev(series: number[]): number | null {
  if (series.length < 2) return null;
  return series[series.length - 1] - series[series.length - 2];
}

function lastN<X>(list: X[], n: number): X[] {
  if (list.length === 0) return [];
  return list.slice(Math.max(0, list.length - n));
}

function maxOf(values: number[]): number {
  return values.reduce((m, v) => Math.max(m, v), 0);
}

function niceCeil(rawMax: number): number {
  if (rawMax <= 4) return 4;
  return Math.ceil(rawMax / 4) * 4;
}

function formatShortDay(iso: string): string {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const month = Number(parts[1]);
  return `${Number(parts[2])} ${SHORT_MONTHS_NL[month - 1] ?? ""}`;
}

function deltaText(t: T, d: number | null): string {
  if (d == null) return t("dashboard.kpi.current");
  const vs = t("dashboard.kpi.vsYesterday");
  if (d > 0) return `▲ ${d} ${vs}`;
  if (d < 0) return `▼ ${-d} ${vs}`;
  return `0 ${vs}`;
}

function formatEuro(amount: number): string {
  if (amount == null) return "€ 0";
  const rounded = Math.round(amount);
  const digits = String(Math.abs(rounded));
  let out = "";
  let count = 0;
  for (let i = digits.length - 1; i >= 0; i--) {
    out = digits.charAt(i) + out;
    if (++count % 3 === 0 && i > 0) out = "." + out;
  }
  return (rounded < 0 ? "-€ " : "€ ") + out;
}

function greetingText(t: T, name: string): string {
  const hour = new Date().getHours();
  let key: string;
  if (hour < 6) key = "dashboard.greeting.night";
  else if (hour < 12) key = "dashboard.greeting.morning";
  else if (hour < 18) key = "dashboard.greeting.afternoon";
  else key = "dashboard.greeting.evening";
  let g = t(key);
  if (!g || g === key) g = t("dashboard.greeting");
  // Capitalise the first letter so a login-name like "admin" greets as "Admin" (parity with Jmix).
  const display = name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
  return display ? `${g}, ${display}` : g;
}

/** Een beginstand klaarzetten voor het scherm waar een melding naartoe gaat; zonder sessionStorage opent het gewoon zonder filter. */
function zetStart(sleutel: string, waarde: string) {
  try {
    sessionStorage.setItem(sleutel, waarde);
  } catch {
    /* geen opslag: het scherm opent ongefilterd */
  }
}

function buildSubtitle(t: T, vestigingNaam: string | null, locale: string): string {
  let sub = t("dashboard.appName");
  if (vestigingNaam) {
    sub += ` · ${vestigingNaam}`;
    // Match the Jmix DateFormatters.formatToTextLong output (e.g. "27 juni 2026") — day-month-year,
    // no weekday — in the operator's resolved locale (no hard-coded locale; see the i18n-formatting rule).
    const today = new Date().toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    if (today) sub += ` · ${today}`;
  }
  return sub;
}
